<script>

import urlState from "@/router/urlState";
import moment from "moment/moment";
import {mapActions, mapState} from "vuex";
import {getProductionDays} from "@/store/utils";

const dayFormat = 'ddd';
const dateFormat = 'MMM D';

export default {
  name: "ProductionSelector",
  mixins: [urlState],
  async mounted() {
    await this.fetchUpcomingProductions();
  },
  methods: {
    ...mapActions(['fetchUpcomingProductions']),
    /** show the production picker; for pages that need a production chosen before they can do anything */
    open() {
      this.showDialog = true;
    },
    closeProductionPicker() {
      this.showDialog = false;
      const {dates: productionDates, production} = this.selectedProduction || {}
      // hand the parent its own copy so it never shares an array with the store
      const dates = productionDates ? [...productionDates] : productionDates;
      console.log('setting dates', dates);
      this.$emit('update:dates', dates);
      this.$emit('update:production', production);
      this.$emit('update', {dates, production});
      if (this.singleDay) {
        this.$emit('update:date', dates[0]);
      }
    },
    allowedDates(val) {
      const notAllowed = {
        0: true,
        // 2: true,
        // 4: true
      };
      return !notAllowed[moment(val).day()];
    },
    closeDatePicker(production) {
      this.showDialog = false;
      this.showDatePicker = false;
      const dates = getProductionDays(this.datePickerDate)
      this.$emit('update:dates', dates);
      this.$emit('update:production', production);
      this.$emit('update', {dates, production});
      if (this.singleDay) {
        this.$emit('update:date', dates[0]);
      }
    },
  },
  watch: {
    showDialog(open) {
      // every close forgets the future productions, however the dialog was dismissed
      if (!open) {
        this.showFutureProduction = false;
      }
    },
  },
  computed: {
    ...mapState(['productions']),
    productionNumber(){
      if (this.production && this.dates && this.dates.length> 0) {
        const productionMap = {
          shipMon: 1,
          localMon: 2,
          CCMon: 3,
          shipThu: 4,
          localThu: 5,
          CCThu: 6
        }
        const dates = this.dates || [];
        const dateFrom = moment(dates[0]);
        const startDay = dateFrom.format(dayFormat);
        return productionMap[`${this.production}${startDay}`] || '?';
      } else {
        return '';
      }
    },
    upcomingProductions() {
      function formatProduction(p) {
        const productionMap = {
          shipMon: 1,
          localMon: 2,
          CCMon: 3,
          shipThu: 4,
          localThu: 5,
          CCThu: 6
        }
        // sort a copy: sorting the store's array in place notifies every watcher of
        // it, and a parent holding that same array would refetch on every render
        const dates = [...p.dates].sort();
        const dateFrom = moment(dates[0]);
        const dateTo = moment(dates[dates.length - 1]);
        const startDay = dateFrom.format(dayFormat);
        const productionNumber = productionMap[`${p.production}${startDay}`] || '?';
        return {
          number: productionNumber,
          text: `${p.production}: ${startDay}-${dateTo.format(dayFormat)}`,
          buttonText: `${p.production}: ${startDay}-${dateTo.format(dayFormat)}`
        };
      }

      const sortKey = a => `${a.dates[0]}-${a.number} `;
      let productions = (this.productions || []).slice();
      if (this.productionFilter) {
        const allowed = [].concat(this.productionFilter);
        productions = productions.filter(p => allowed.includes(p.production));
      }
      return productions.map(p => ({...p, ...formatProduction(p)}))
        .sort((a, b) => sortKey(a).localeCompare(sortKey(b)));
    },
    productionColumns() {
      const grouped = {};
      this.upcomingProductions.forEach(p => {
        const dateFrom = moment(p.dates[0]);
        const dateTo = moment(p.dates[p.dates.length - 1]);
        const day = dateFrom.format(dayFormat);
        const key = dateFrom.format('YYYY-MM-DD');
        
        if (!grouped[key]) {
          grouped[key] = {
            title: `${day}-${dateTo.format(dayFormat)} (${dateFrom.format(dateFormat)}-${dateTo.format(dateFormat)})`,
            productions: []
          };
        }
        grouped[key].productions.push(p);
      });
      
      return Object.values(grouped).sort((a, b) => a.productions[0].dates[0].localeCompare(b.productions[0].dates[0]));
    },
    /** the soonest production week only, unless the future productions have been asked for */
    visibleProductionColumns() {
      return this.showFutureProduction ? this.productionColumns : this.productionColumns.slice(0, 1);
    },
    hasFutureProductions() {
      return this.productionColumns.length > 1;
    },
    dateFrom() {
      return [...(this.dates || [])].sort()[0];
    },
    dateTo() {
      return [...(this.dates || [])].sort().reverse()[0];
    },
    productionGroups() {
      return [...new Set(this.productions.map(p => p.production))];
    },
    datesFormatted() {
      const format = dateFormat;
      if (!this.dateFrom) {
        return '';
      } else if (this.dateFrom === this.dateTo) {
        return `${moment(this.dateFrom).format(format)}`;
      } else {
        return `${moment(this.dateFrom).format(format)} - ${moment(this.dateTo).format(format)}`
      }
    },
    selectedProductionText() {
      if (this.production && this.dates && this.dates.length > 0) {
        const dates = this.dates.slice().sort();
        const dateFrom = moment(dates[0]);
        const dateTo = moment(dates[dates.length - 1]);
        const startDay = dateFrom.format(dayFormat);
        const endDay = dateTo.format(dayFormat);
        return `${this.production}: ${startDay}-${endDay} (${dateFrom.format(dateFormat)}-${dateTo.format(dateFormat)})`;
      }
      return '';
    },
  },
  data() {
    return {
      datePickerDate: null,
      showDialog: false,
      showFutureProduction: false,
      selectedProduction: null,
      showDatePicker: null
    }
  },
  props: {
    production: {required: false},
    dates: {type: Array},
    date: {type: String},
    singleDay: {type: Boolean, default: false},
    productionFilter: {type: [String, Array], default: null, required: false}
  }
}

</script>

<template>
  <div :class="`${production}-production`">
    <v-text-field
      class="px-2"
      :value="selectedProductionText"
      label="Production Cycle - Filter by kitchen production schedules"
      hide-details
      single-line
      readonly
      :prefix="productionNumber?`(${productionNumber})`:''"
      @click="showDialog=true"
      append-icon="mdi-calendar"
      prepend-inner-icon="mdi-chef-hat"
      style="min-width: 350px;"
    />
    <v-dialog
      v-model="showDialog"
      ref="dialog"
      width="auto"
      class="debugcss"
    >
      <v-card v-if="!showDatePicker">
        <v-card-title>
          <v-spacer/>
          Select Production Cycle
          <v-spacer/>
          <v-btn icon @click="showDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-actions>
          <v-row>
            <v-col
              :cols="visibleProductionColumns.length > 1 ? 6 : 12"
              v-for="(column, i) of visibleProductionColumns"
              v-bind:key="i"
            >
              <div class="text-center mb-2" style="white-space: nowrap; font-size: 0.8em;">
                <strong>{{ column.title }}</strong>
              </div>
              <div class="flex-column">
                <div class="mb-6" v-for="(p,j) of column.productions" v-bind:key="j">
                  <v-btn
                    width="100%"
                    :class="`${p.production}-production`"
                    outlined large @click="selectedProduction = p; closeProductionPicker()"
                  >
                    <v-chip style="border-width: thick" v-if="p.production==='ship'" outlined color="blue">
                      {{ p.number }}
                    </v-chip>
                    <v-chip style="border-width: thick" v-if="p.production==='local'" outlined>{{
                        p.number
                      }}
                    </v-chip>
                    <v-chip style="border-width: thick" v-if="p.production==='CC'" outlined color="green">{{
                        p.number
                      }}
                    </v-chip>
                    <div class="text-right pl-4" style="width: 100%">
                      {{ p.buttonText }}
                    </div>
                  </v-btn>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-actions>
        <v-card-actions>
          <v-spacer/>
          <v-btn outlined x-small @click="showDatePicker=true; datePickerDate=dates[0]">Show Other Dates</v-btn>
          <v-btn
            v-if="hasFutureProductions"
            class="ml-2"
            outlined
            x-small
            @click="showFutureProduction = !showFutureProduction"
          >
            {{ showFutureProduction ? 'Hide Future Production' : 'Show Future Production' }}
          </v-btn>
          <v-spacer/>
        </v-card-actions>
      </v-card>
      <v-card v-if="showDatePicker">
        <v-card-text>
          <v-date-picker
            v-model="datePickerDate"
            no-title
            :allowed-dates="allowedDates"
            @close="closeDatePicker"
          ></v-date-picker>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn v-for="p of productionGroups" v-bind:key="p"
                 @click="closeDatePicker(p)"
          >
            {{ p }}
          </v-btn>
          <v-spacer/>
          <!--          <v-btn @click="closeDatePicker">Close</v-btn>-->
        </v-card-actions>
        <v-card-actions class="mt-10">
          <v-spacer/>
          <v-btn outlined x-small @click="showDatePicker=false">Show Upcoming Productions</v-btn>
          <v-spacer/>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>

</style>